import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const services = [
  { label: "Démoussage", path: "/services/demoussage" },
  { label: "Démoussage monuments & collectivités", path: "/services/demoussage-monuments" },
  { label: "Inspection toiture", path: "/services/inspection" },
  { label: "Inspection bâtiments & gouttières", path: "/services/inspection-batiments" },
  { label: "Nettoyage de bardage d'immeuble", path: "/services/nettoyage-bardage" },
  { label: "Cartographie & mesure", path: "/services/cartographie-drone" },
  { label: "Nettoyage en hauteur", path: "/services/nettoyage-hauteur" },
  { label: "Inspection thermographique", path: "/services/inspection-thermographique" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;
  const isServicesActive = location.pathname.startsWith("/services");

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-navbar">
      <div className="container mx-auto">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/logo-normandy-drone.png"
              alt="Normandy Drone - Démoussage et inspection de toiture par drone"
              className="h-14 w-14 rounded-full"
            />
            <span className="hidden sm:block text-lg font-bold text-white">
              Normandy Drone
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className={cn(
                "text-sm font-medium transition-colors hover:text-secondary",
                isActive("/") ? "text-secondary" : "text-white/90"
              )}
            >
              Accueil
            </Link>

            {/* Services Dropdown */}
            <div className="relative group">
              <button
                className={cn(
                  "flex items-center gap-1 text-sm font-medium transition-colors hover:text-secondary",
                  isServicesActive ? "text-secondary" : "text-white/90"
                )}
              >
                Services
                <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
              </button>
              <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="glass-card p-2 min-w-[280px]">
                  {services.map((service) => (
                    <Link
                      key={service.path}
                      to={service.path}
                      className={cn(
                        "block px-4 py-2.5 rounded-lg text-sm transition-colors hover:bg-secondary hover:text-black",
                        isActive(service.path)
                          ? "text-secondary bg-secondary/20"
                          : "text-foreground"
                      )}
                    >
                      {service.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link
              to="/zone-intervention"
              className={cn(
                "text-sm font-medium transition-colors hover:text-secondary",
                isActive("/zone-intervention") ? "text-secondary" : "text-white/90"
              )}
            >
              Zone d'intervention
            </Link>

            <Link
              to="/faq"
              className={cn(
                "text-sm font-medium transition-colors hover:text-secondary",
                isActive("/faq") ? "text-secondary" : "text-white/90"
              )}
            >
              FAQ
            </Link>

            <Link
              to="/contact"
              className={cn(
                "text-sm font-medium transition-colors hover:text-secondary",
                isActive("/contact") ? "text-secondary" : "text-white/90"
              )}
            >
              Contact
            </Link>
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:0760268944"
              className="flex items-center gap-2 text-sm font-medium text-white/90 hover:text-secondary transition-colors"
            >
              <Phone className="h-4 w-4" />
              07 60 26 89 44
            </a>
            <Button variant="accent" size="sm" asChild>
              <Link to="/contact">Devis gratuit</Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-border/30 animate-fade-in">
            <div className="flex flex-col gap-2">
              <Link
                to="/"
                onClick={() => setIsOpen(false)}
                className={cn(
                  "px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                  isActive("/") ? "bg-secondary/10 text-secondary" : "text-white/90"
                )}
              >
                Accueil
              </Link>

              {/* Mobile Services */}
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className={cn(
                  "flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                  isServicesActive ? "bg-secondary/10 text-secondary" : "text-white/90"
                )}
              >
                Services
                <ChevronDown className={cn("h-4 w-4 transition-transform", servicesOpen && "rotate-180")} />
              </button>
              {servicesOpen && (
                <div className="pl-4 flex flex-col gap-1">
                  {services.map((service) => (
                    <Link
                      key={service.path}
                      to={service.path}
                      onClick={() => setIsOpen(false)}
                      className={cn(
                        "px-4 py-2.5 rounded-lg text-sm transition-colors hover:bg-secondary hover:text-black",
                        isActive(service.path)
                          ? "text-secondary bg-secondary/20"
                          : "text-white/90"
                      )}
                    >
                      {service.label}
                    </Link>
                  ))}
                </div>
              )}

              <Link
                to="/zone-intervention"
                onClick={() => setIsOpen(false)}
                className={cn(
                  "px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                  isActive("/zone-intervention") ? "bg-secondary/10 text-secondary" : "text-white/90"
                )}
              >
                Zone d'intervention
              </Link>

              <Link
                to="/faq"
                onClick={() => setIsOpen(false)}
                className={cn(
                  "px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                  isActive("/faq") ? "bg-secondary/10 text-secondary" : "text-white/90"
                )}
              >
                FAQ
              </Link>

              <Link
                to="/contact"
                onClick={() => setIsOpen(false)}
                className={cn(
                  "px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                  isActive("/contact") ? "bg-secondary/10 text-secondary" : "text-white/90"
                )}
              >
                Contact
              </Link>

              <div className="pt-4 px-4 flex flex-col gap-3">
                <a
                  href="tel:0760268944"
                  className="flex items-center justify-center gap-2 text-sm font-medium text-white/90"
                >
                  <Phone className="h-4 w-4" />
                  07 60 26 89 44
                </a>
                <Button variant="accent" asChild>
                  <Link to="/contact" onClick={() => setIsOpen(false)}>
                    Devis gratuit
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
